import React from "react";
import { set } from "lodash";
import { useTranslation } from "react-i18next";
import { Button } from "../Button";
import { DatePicker } from "../DatePicker";
import { HoursSelector } from "../HoursSelector";
import { Checkbox } from "../Checkbox";
import { TimezoneSelector } from "../TimezoneSelector";
import { convertTimeFormatToClockFormat } from "@/utils/dateTimeStringifyHelper";
import { EventDateTime, EventTimeFormat } from "@/types/qr";
import { PAGES } from "@/internals/constants/pages";
import { CtaStyle } from "../Button/types";
import "./styles.scss";
import { InputAdapter } from "../Input/adapter/InputAdapter";

export function EventDateAndTime({
  defaultValue,
  onClick,
  ctaStyle,
}: {
  defaultValue: EventDateTime;
  onClick?: (arg: Partial<EventDateTime>) => void;
  ctaStyle?: CtaStyle;
}) {
  const { t: translate } = useTranslation(PAGES.QR_EVENT);

  const [format, setTimeFormat] = React.useState<EventTimeFormat>(defaultValue?.format || "AM-PM");
  const [isAllDay, setIsAllDay] = React.useState<boolean>(defaultValue?.allDay || false);

  React.useEffect(() => {
    return onClick && onClick({ format });
  }, [format]);

  React.useEffect(() => {
    onClick && onClick({ allDay: isAllDay });
  }, [isAllDay]);

  const onInputChangeHandler = ({ name, value }) => {
    if (Array.isArray(name)) {
      const newObject = set({}, name, value);
      return onClick && onClick(newObject);
    }

    return onClick && onClick({ [name]: value });
  };

  return (
    <div className="event-date-time">
      <div className="event-date-time__format">
        <Button
          type="button"
          label={translate("time_am_pm_button")}
          tertiary
          onClick={() => setTimeFormat("AM-PM")}
          rounded={!!ctaStyle}
          className={`${format === "24-HOURS" && "hours__format--disabled"}`}
        />

        <Button
          type="button"
          label={translate("time_24_hours_button")}
          tertiary
          onClick={() => setTimeFormat("24-HOURS")}
          rounded={!!ctaStyle}
          className={`${format === "AM-PM" && "hours__format--disabled"}`}
        />
      </div>

      <div className="event-date-time__date-time-row">
        <DatePicker
          type="single"
          minDate="today"
          label={translate("time_start_label")}
          inputPlaceholder={translate("time_start_placeholder")}
          defaultDate={defaultValue?.start?.date ? [new Date(defaultValue?.start?.date)] : []}
          fluid
          disableMobile
          className={"event-date-time__date-picker"}
          onChangeDate={([date] = []) =>
            onInputChangeHandler({ name: ["start", "date"], value: date })
          }
        />

        {!isAllDay && (
          <HoursSelector
            value={defaultValue?.start?.time}
            name={"start"}
            placeholder={format === "AM-PM" ? "00:00am" : "00:00h"}
            disabled={isAllDay}
            timeFormat={convertTimeFormatToClockFormat(format)}
            onSelectHour={({ name, value }) =>
              onInputChangeHandler({ name: [name, "time"], value })
            }
          />
        )}
      </div>

      <div className="event-date-time__date-time-row">
        <DatePicker
          type="single"
          minDate={defaultValue?.start?.date ? new Date(defaultValue?.start?.date) : "today"}
          label={translate("time_end_label")}
          inputPlaceholder={translate("time_end_placeholder")}
          defaultDate={defaultValue?.end?.date ? [new Date(defaultValue?.end?.date)] : []}
          fluid
          disableMobile
          className={"event-date-time__date-picker"}
          onChangeDate={([date] = []) =>
            onInputChangeHandler({ name: ["end", "date"], value: date })
          }
        />

        {!isAllDay && (
          <HoursSelector
            value={defaultValue?.end?.time}
            name={"end"}
            placeholder={format === "AM-PM" ? "00:00am" : "00:00h"}
            disabled={isAllDay}
            timeFormat={convertTimeFormatToClockFormat(format)}
            onSelectHour={({ name, value }) =>
              onInputChangeHandler({ name: [name, "time"], value })
            }
          />
        )}
      </div>

      <div className="event-date-time__timezone-row">
        <Checkbox
          id={"event-all-day-checkbox"}
          label={translate("time_all_day_label")}
          className="event-date-time__timezone-checkbox"
          defaultValue={isAllDay}
          onChange={(e) => setIsAllDay(e.target.checked)}
        />
        {!isAllDay && (
          <TimezoneSelector
            placeholder="Select Timezone"
            label={translate("time_timezone_label")}
            value={defaultValue?.timezone}
            searchPlaceholder={translate("search_input_placeholder")}
            onChange={(timezone) => onInputChangeHandler({ name: "timezone", value: timezone })}
          />
        )}
      </div>
      <InputAdapter
        id="timeActionTitle"
        type="text"
        name="timeActionTitle"
        dataQA="timeActionTitle"
        label={translate("time_button_label")}
        placeholder={translate("time_button_placeholder")}
        defaultValue={defaultValue?.actionTitle}
        helper={{
          item: "help",
          tooltip: {
            text: translate("time_button_tooltip"),
            position: "top",
          },
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onInputChangeHandler({ name: "actionTitle", value: e.target.value })
        }
      />
    </div>
  );
}
